import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Globe, 
  Brain, 
  Upload, 
  Play, 
  Square, 
  RefreshCw, 
  Download,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  DollarSign,
  ArrowLeft,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FineTuningPage = () => {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [costEstimate, setCostEstimate] = useState(null);
  
  // Form state
  const [selectedTrainFile, setSelectedTrainFile] = useState('');
  const [selectedValFile, setSelectedValFile] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [modelSuffix, setModelSuffix] = useState('meteo-translate');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [filesRes, jobsRes, modelsRes] = await Promise.all([
        fetch(`${API}/finetune/files`, { headers }).then(r => r.json()),
        fetch(`${API}/finetune/jobs`, { headers }).then(r => r.json()),
        fetch(`${API}/finetune/models`, { headers }).then(r => r.json()).catch(() => ({ models: [] }))
      ]);
      
      setFiles(filesRes.files || []);
      setJobs(jobsRes.jobs || []);
      setModels(modelsRes.models || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load fine-tuning data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostEstimate = async (filename) => {
    try {
      const res = await fetch(`${API}/finetune/cost-estimate?training_file=${encodeURIComponent(filename)}`, { headers });
      const data = await res.json();
      setCostEstimate(data);
    } catch (error) {
      console.error('Failed to estimate cost:', error);
    }
  };

  const handleFileSelect = (filename) => {
    setSelectedTrainFile(filename);
    fetchCostEstimate(filename);
    
    // Auto-select matching validation file
    const valFile = filename.replace('_train_', '_val_');
    const matchingVal = files.find(f => f.name === valFile);
    if (matchingVal) {
      setSelectedValFile(valFile);
    }
  };

  const handleExecute = async () => {
    if (!selectedTrainFile) {
      toast.error('Please select a training file');
      return;
    }

    setExecuting(true);
    try {
      const res = await fetch(`${API}/finetune/execute`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          training_file: selectedTrainFile,
          validation_file: selectedValFile || null,
          model: selectedModel,
          suffix: modelSuffix
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Fine-tuning job started: ${data.job_id}`);
        setExecuteDialogOpen(false);
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to start fine-tuning');
      }
    } catch (error) {
      toast.error('Failed to execute fine-tuning');
    } finally {
      setExecuting(false);
    }
  };

  const refreshJobStatus = async (jobId) => {
    try {
      const res = await fetch(`${API}/finetune/jobs/${jobId}`, { headers });
      const data = await res.json();
      
      // Update local state
      setJobs(jobs.map(j => j.job_id === jobId ? { ...j, ...data } : j));
      toast.success(`Job ${jobId}: ${data.status}`);
    } catch (error) {
      toast.error('Failed to refresh job status');
    }
  };

  const cancelJob = async (jobId) => {
    if (!confirm('Are you sure you want to cancel this job?')) return;
    
    try {
      const res = await fetch(`${API}/finetune/jobs/${jobId}/cancel`, {
        method: 'POST',
        headers
      });
      
      if (res.ok) {
        toast.success('Job cancelled');
        fetchData();
      } else {
        toast.error('Failed to cancel job');
      }
    } catch (error) {
      toast.error('Failed to cancel job');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running':
      case 'validating_files':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'cancelled':
        return <Square className="w-5 h-5 text-gray-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Globe className="w-6 h-6 text-blue-600" />
            <span className="font-bold text-xl tracking-tight text-gray-900">MeteoTranslate</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/translate" className="text-sm font-medium text-gray-600 hover:text-gray-900">Translate</Link>
            <Link to="/rag-admin" className="text-sm font-medium text-gray-600 hover:text-gray-900">RAG Admin</Link>
            <Link to="/finetune" className="text-sm font-medium text-blue-600">Fine-Tuning</Link>
            <Link to="/settings" className="text-sm font-medium text-gray-600 hover:text-gray-900">Settings</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Link */}
        <Link to="/rag-admin" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to RAG Admin
        </Link>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-purple-600" />
              Fine-Tuning Manager
            </h1>
            <p className="mt-2 text-gray-600">
              Train custom OpenAI models on meteorological translation data
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => setExecuteDialogOpen(true)}>
              <Play className="w-4 h-4 mr-2" />
              Start Fine-Tuning
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Training Files */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Training Files
              </CardTitle>
              <CardDescription>
                Prepared JSONL files ready for fine-tuning
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : files.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">
                  No training files found. Upload TMX data first.
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {files.filter(f => f.name.endsWith('.jsonl')).map((file, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)} • {file.examples || '?'} examples
                        </p>
                      </div>
                      <a
                        href={`${API}/finetune/download/${file.name}`}
                        className="p-2 hover:bg-gray-200 rounded"
                        title="Download"
                      >
                        <Download className="w-4 h-4 text-gray-500" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fine-Tuned Models */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                Fine-Tuned Models
              </CardTitle>
              <CardDescription>
                Successfully trained custom models
              </CardDescription>
            </CardHeader>
            <CardContent>
              {models.length === 0 ? (
                <div className="text-center py-8">
                  <Brain className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">
                    No fine-tuned models yet. Start a training job!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {models.map((model, i) => (
                    <div key={i} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="font-mono text-sm font-medium">{model.model_id}</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Base: {model.base_model} • Trained tokens: {model.trained_tokens?.toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Jobs Table */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Fine-Tuning Jobs
            </CardTitle>
            <CardDescription>
              Track the status of your training jobs
            </CardDescription>
          </CardHeader>
          <CardContent>
            {jobs.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                No fine-tuning jobs yet. Click "Start Fine-Tuning" to begin.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Job ID</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Model</th>
                      <th className="text-left py-3 px-4 font-medium">Created</th>
                      <th className="text-left py-3 px-4 font-medium">Fine-Tuned Model</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-xs">{job.job_id}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(job.status)}
                            <span className="capitalize">{job.status}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">{job.model}</td>
                        <td className="py-3 px-4 text-gray-500">
                          {new Date(job.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 font-mono text-xs">
                          {job.fine_tuned_model || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => refreshJobStatus(job.job_id)}
                              className="p-1.5 hover:bg-gray-100 rounded"
                              title="Refresh status"
                            >
                              <RefreshCw className="w-4 h-4 text-gray-500" />
                            </button>
                            {['queued', 'validating_files', 'running'].includes(job.status) && (
                              <button
                                onClick={() => cancelJob(job.job_id)}
                                className="p-1.5 hover:bg-red-50 rounded"
                                title="Cancel job"
                              >
                                <Square className="w-4 h-4 text-red-500" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Execute Dialog */}
      <Dialog open={executeDialogOpen} onOpenChange={setExecuteDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5" />
              Start Fine-Tuning Job
            </DialogTitle>
            <DialogDescription>
              Configure and start a new fine-tuning job on OpenAI
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Training File */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Training File *</label>
              <Select value={selectedTrainFile} onValueChange={handleFileSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select training file..." />
                </SelectTrigger>
                <SelectContent>
                  {files.filter(f => f.name.includes('_train_')).map((f, i) => (
                    <SelectItem key={i} value={f.name}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Validation File */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Validation File (Optional)</label>
              <Select value={selectedValFile} onValueChange={setSelectedValFile}>
                <SelectTrigger>
                  <SelectValue placeholder="Select validation file..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {files.filter(f => f.name.includes('_val_')).map((f, i) => (
                    <SelectItem key={i} value={f.name}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Base Model */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Base Model</label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o-mini">gpt-4o-mini ($3/1M tokens)</SelectItem>
                  <SelectItem value="gpt-4o">gpt-4o ($25/1M tokens)</SelectItem>
                  <SelectItem value="gpt-3.5-turbo">gpt-3.5-turbo ($8/1M tokens)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Model Suffix */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Model Suffix</label>
              <Input
                value={modelSuffix}
                onChange={(e) => setModelSuffix(e.target.value)}
                placeholder="meteo-translate"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your model will be named: ft:{selectedModel}:your-org::{modelSuffix}
              </p>
            </div>

            {/* Cost Estimate */}
            {costEstimate && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-900">Cost Estimate</span>
                </div>
                <div className="text-sm text-amber-800 space-y-1">
                  <p>Training examples: {costEstimate.num_examples?.toLocaleString()}</p>
                  <p>Estimated tokens: {costEstimate.training_tokens?.toLocaleString()}</p>
                  <p className="font-medium">
                    Estimated cost ({selectedModel}): {costEstimate.cost_estimates?.[selectedModel]}
                  </p>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                Fine-tuning will upload data to OpenAI and incur charges based on your OpenAI account.
                Make sure you have a valid OPENAI_API_KEY configured.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExecuteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExecute} disabled={executing || !selectedTrainFile}>
              {executing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Start Fine-Tuning</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FineTuningPage;
